/**
 * Zeit und Zeitzonen.
 *
 * Grundregel des Projekts: **gespeichert wird UTC, gerechnet wird in der Zeitzone
 * des Nutzers.** Wer Fälligkeiten in UTC ausrechnet, verschiebt zur Zeitumstellung
 * jede Erinnerung um eine Stunde — und merkt es zweimal im Jahr an einem Sonntag.
 *
 * Bewusst ohne Bibliothek: `Intl` kennt die Zeitzonendatenbank bereits, und die
 * gesamte Umrechnung sind zwei Dutzend Zeilen, die hier getestet danebenliegen.
 */

/** Kalendertag ohne Uhrzeit, `YYYY-MM-DD`. */
export type IsoDate = string;
/** Wanduhrzeit ohne Zeitzone, `HH:MM`. */
export type WallTime = string;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function isIsoDate(value: string): value is IsoDate {
  return DATE_RE.test(value);
}

export function isWallTime(value: string): value is WallTime {
  return TIME_RE.test(value);
}

/**
 * Verschiebung der Zeitzone gegenüber UTC zum gegebenen Zeitpunkt, in Millisekunden.
 * Positiv östlich von Greenwich (Europe/Berlin: +1 h Winter, +2 h Sommer).
 */
export function zoneOffsetMs(instant: Date, timeZone: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  const parts: Record<string, number> = {};
  for (const part of dtf.formatToParts(instant)) {
    if (part.type !== 'literal') parts[part.type] = Number(part.value);
  }

  const asIfUtc = Date.UTC(
    parts.year ?? 1970,
    (parts.month ?? 1) - 1,
    parts.day ?? 1,
    parts.hour ?? 0,
    parts.minute ?? 0,
    parts.second ?? 0,
  );

  return asIfUtc - instant.getTime();
}

/**
 * Wanduhrzeit in einer Zeitzone → Zeitpunkt in UTC.
 *
 * Zwei Durchgänge, weil die Verschiebung selbst vom Zeitpunkt abhängt: der erste
 * schätzt, der zweite korrigiert an der Zeitumstellung.
 *
 * Randfälle der Umstellung (Europe/Berlin):
 * - **Frühjahr**, 02:00 → 03:00: 02:30 existiert nicht. Ergebnis ist 03:30 Ortszeit
 *   — die Erinnerung fällt nach vorn statt auszufallen. Ausfallen wäre schlimmer.
 * - **Herbst**, 03:00 → 02:00: 02:30 existiert zweimal. Ergebnis ist das **erste**
 *   Vorkommen (noch Sommerzeit). Einmal erinnern, nicht zweimal.
 */
export function wallTimeToUtc(date: IsoDate, time: WallTime, timeZone: string): Date {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const [hh, mm] = time.split(':').map(Number) as [number, number];

  const naive = Date.UTC(y, m - 1, d, hh, mm, 0, 0);

  const firstGuess = new Date(naive - zoneOffsetMs(new Date(naive), timeZone));
  const corrected = new Date(naive - zoneOffsetMs(firstGuess, timeZone));

  return corrected;
}

/** Kalendertag, auf den ein Zeitpunkt in der gegebenen Zeitzone fällt. */
export function isoDateInZone(instant: Date, timeZone: string): IsoDate {
  const dtf = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return dtf.format(instant);
}

/** Wanduhrzeit, die ein Zeitpunkt in der gegebenen Zeitzone zeigt. */
export function wallTimeInZone(instant: Date, timeZone: string): WallTime {
  const dtf = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hourCycle: 'h23',
    hour: '2-digit',
    minute: '2-digit',
  });
  return dtf.format(instant);
}

/** Kalendertag um `days` verschieben — ohne Zeitzonen-Einfluss, reine Datumsarithmetik. */
export function addDays(date: IsoDate, days: number): IsoDate {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const shifted = new Date(Date.UTC(y, m - 1, d + days));
  return shifted.toISOString().slice(0, 10);
}

export function addMinutes(instant: Date, minutes: number): Date {
  return new Date(instant.getTime() + minutes * 60_000);
}

/** Wochentag in der Zeitzone: 1 = Montag … 7 = Sonntag (ISO-8601). */
export function isoWeekdayInZone(instant: Date, timeZone: string): number {
  const short = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short' }).format(instant);
  const order = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const index = order.indexOf(short);
  return index === -1 ? 1 : index + 1;
}
